import { Role } from "./roles";

/**
 * The worklist's tab keys, in display order - matches containers/worklist.js's
 * `tabList` array exactly (name/key pairs: Patient Submitted, New Admissions,
 * New Wounds, PI Assessments, Other Assessments, Wound Expert, Completed
 * Assessments).
 */
export const WORKLIST_TAB_KEYS = [
  "patientSubmitted",
  "newAdmissions",
  "newWounds",
  "piAssessments",
  "otherAssessments",
  "woundExpert",
  "completedAssessments",
] as const;

export type WorklistTabKey = (typeof WORKLIST_TAB_KEYS)[number];

/**
 * Role -> ordered list of visible tab keys. Replaces rbac.js's
 * worklistTabRbac positional boolean array (fragile: a tab's visibility was
 * implied only by its index matching containers/worklist.js's tabList order,
 * with no name tying the two together). No behavior change - this is a
 * verbatim port of the original boolean table.
 */
export const WORKLIST_TAB_PERMISSIONS: Record<Role, WorklistTabKey[]> = {
  MedlineAdministrator: [...WORKLIST_TAB_KEYS],
  MedlineStandardUser: [...WORKLIST_TAB_KEYS],
  MedlineSupport: [...WORKLIST_TAB_KEYS],
  ExternalAdministrator: [...WORKLIST_TAB_KEYS],
  ExternalStandardUser: ["patientSubmitted", "newAdmissions", "newWounds", "completedAssessments"],
  ExternalNE1WoundExpert: [...WORKLIST_TAB_KEYS],
  ExternalNE1Review: ["patientSubmitted", "newAdmissions", "newWounds", "piAssessments", "otherAssessments", "completedAssessments"],
  ExternalNE1Assesment: ["patientSubmitted", "newAdmissions", "newWounds", "piAssessments", "otherAssessments", "completedAssessments"],
  ExternalNE1ReadOnly: [],
};

/**
 * woundPhotosCount === '0' hides the first tab ("Patient Submitted")
 * regardless of role - verbatim port of worklistTabRbac's special-case for
 * index 0.
 */
export const getVisibleWorklistTabKeys = (role: Role, woundPhotosCount?: string): WorklistTabKey[] => {
  const roleTabs = WORKLIST_TAB_PERMISSIONS[role] ?? [];
  if (woundPhotosCount === "0") {
    return roleTabs.filter((key) => key !== "patientSubmitted");
  }
  return roleTabs;
};
